// layouts/lang-layout.tsx
import { useEffect, useMemo } from "react";
import { Outlet, useParams, Navigate } from "react-router-dom";

// Store
import { useAppDispatch } from "store/store";
import { setLanguage } from "store/slices/language-slice";

// Language configuration
import { getLanguageFromUrlSegment, getHtmlLang, getTextDirection, DEFAULT_LANGUAGE, getUrlSegmentForLanguage } from "config/language-config";

function updateDocumentLanguage(languageCode: string): void {
	const htmlLang = getHtmlLang(languageCode);
	const dir = getTextDirection(languageCode);

	document.documentElement.setAttribute("lang", htmlLang);
	document.documentElement.setAttribute("dir", dir);
	document.documentElement.setAttribute("data-rh", "lang");
}

export default function LangLayout() {
	const { lang } = useParams<{ lang?: string }>();
	const dispatch = useAppDispatch();

	// Get the canonical language code from the URL segment
	const languageCode = useMemo(() => {
		return lang ? getLanguageFromUrlSegment(lang) : null;
	}, [lang]);

	// Determine if we need to redirect (invalid language segment)
	const shouldRedirect = !languageCode;

	// Set HTML attributes and update Redux when language changes
	// This effect runs for BOTH valid languages AND before redirect
	useEffect(() => {
		if (languageCode) {
			// Valid language - update to the resolved language
			updateDocumentLanguage(languageCode);
			dispatch(setLanguage(languageCode));
		} else if (shouldRedirect) {
			// Invalid language segment - update Redux to default BEFORE redirect
			// This ensures the dropdown shows the correct language after redirect
			updateDocumentLanguage(DEFAULT_LANGUAGE);
			dispatch(setLanguage(DEFAULT_LANGUAGE));
		}
	}, [languageCode, shouldRedirect, dispatch]);

	// If invalid language segment, redirect to default language
	if (shouldRedirect) {
		const defaultSegment = getUrlSegmentForLanguage(DEFAULT_LANGUAGE);
		return <Navigate to={`/${defaultSegment}`} replace />;
	}

	return <Outlet />;
}
